import { DropImageSettingsDTO } from 'business/models/Models';
import CaptureProfileService from 'business/services/CaptureProfileService';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import useCurrentRegionIds from 'CustomHooks/useCurrentRegionIds';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRMSelector } from 'redux/hooks';
import { useUpdateExpRegionCaptureProfiles } from 'redux/hooks/experiment';
import {
  selectExpId,
  selectExpImagingScheduleHeaders,
} from 'redux/selectors/ExperimentStateSelector';

interface Props {
  customizeSettingsClassName: string;
  imageSettingsList: string[];
  setDefaultRegionCaptureProfile: (value?: string, isNone?: boolean) => Promise<void>;
  isMenuOpen: boolean;
  wrapperRef: React.RefObject<HTMLDivElement>;
  setIsMenuOpen: (open: boolean) => void;
  canvasMode?: boolean;
}
const captureProfileService = new CaptureProfileService();

const DropImageSettingsDropdown = (props: Props) => {
  const {
    customizeSettingsClassName,
    imageSettingsList,
    setDefaultRegionCaptureProfile,
    isMenuOpen,
    wrapperRef,
    setIsMenuOpen,
    canvasMode,
  } = props;
  const regionIds: number[] = useCurrentRegionIds();
  const expId = useRMSelector(selectExpId);
  const imagingSchedules = useRMSelector(selectExpImagingScheduleHeaders);
  const updateRegionCaptureProfiles = useUpdateExpRegionCaptureProfiles();
  const [dropImageSettingsList, setDropImageSettingsList] = useState<
    DropImageSettingsDTO[]
  >([]);
  const dropImageSettingsListRef = useRef(dropImageSettingsList);
  const menuPortalRef = useRef<HTMLDivElement>(null);
  const [openSubmenuId, setOpenSubmenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(
    null,
  );

  const updateMenuPosition = useCallback(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, []);

  useEffect(() => {
    if (isMenuOpen && wrapperRef.current) {
      updateMenuPosition();
    } else {
      setMenuPosition(null);
    }
  }, [isMenuOpen, updateMenuPosition]);

  const imageSettingsAvailable = imagingSchedules?.map((is) => {
    const filteredImageSettingsList = imageSettingsList.filter((is) => {
      const matchingProfile = dropImageSettingsList?.find(
        (dis) => dis.captureProfileName === is && dis.isCustomizedDeleted,
      );
      return is !== matchingProfile?.captureProfileName;
    });

    const isChecked =
      filteredImageSettingsList.some((imgSetting) => imgSetting === is) ||
      dropImageSettingsList.some(
        (imgSetting) =>
          imgSetting.captureProfileName === is && !imgSetting.isCustomizedDeleted,
      );

    return {
      label: is,
      checkbox: (
        <input
          id="commandpanel-exptools-dropimagesettingsdropdown-available-checkbox"
          type="checkbox"
          checked={isChecked}
          className="rounded-sm w-6 h-6"
          onChange={(e) => onChangeDropImageSettings(e, is, true)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    };
  });

  const customizeOptions = [
    {
      id: 1,
      label: 'Same as Experiment',
      isDisabled: false,
      onClick: () => setDefaultRegionCaptureProfile(),
    },
    {
      id: 2,
      label: 'Customize Settings',
      submenuItems: imageSettingsAvailable,
      isDisabled: false,
      className: customizeSettingsClassName
        ? customizeSettingsClassName
        : 'multilevel-submenu top-3 right-1',
    },
    {
      id: 3,
      label: 'None',
      isDisabled: false,
      onClick: () => setDefaultRegionCaptureProfile('', true),
    },
  ];

  const onChangeDropImageSettings = (e, value, isCheckbox = false) => {
    const newImageSettings = [...dropImageSettingsList];
    if (isCheckbox && e.target.checked) {
      // add new region capture profile
      const addNewRegionCaptureProfile = new DropImageSettingsDTO({
        expId: expId,
        regionIds: regionIds,
        isCustomized: true,
        isNone: false,
        isCustomizedDeleted: false,
        captureProfileName: value,
      });
      newImageSettings.push(addNewRegionCaptureProfile);
      setDropImageSettingsList(newImageSettings);
    } else if (isCheckbox && !e.target.checked) {
      const itemToUpdate = newImageSettings.find(
        (item) => item.captureProfileName === value,
      );
      if (itemToUpdate) {
        itemToUpdate.isCustomizedDeleted = true;
      }
      if (itemToUpdate && !newImageSettings.some((is) => !is.isCustomizedDeleted)) {
        //default case: same as experiment
        setDropImageSettingsList([]);
        setDefaultRegionCaptureProfile(value);
      } else if (!itemToUpdate) {
        const rcp = new DropImageSettingsDTO({
          expId: expId,
          regionIds: regionIds,
          isCustomized: true,
          isNone: false,
          isCustomizedDeleted: true,
          captureProfileName: value,
        });
        newImageSettings.push(rcp);
        setDropImageSettingsList(newImageSettings);
      } else {
        setDropImageSettingsList(newImageSettings);
      }
    }
  };

  const onMenuClick = () => {
    setIsMenuOpen(!isMenuOpen);
    setOpenSubmenuId(null); // Close any open submenu when the main menu is toggled
  };

  const handleOptionClick = (option) => {
    if (option.submenuItems) {
      option.id === openSubmenuId && updateRegionProfiles();
      setOpenSubmenuId(option.id === openSubmenuId ? null : option.id); // Toggle submenu
    } else {
      if (option.onClick) {
        option.onClick();
      }
    }
  };

  const updateRegionProfiles = async () => {
    if (dropImageSettingsListRef?.current?.length) {
      try {
        const resp = await captureProfileService.updateRegionCaptureProfile(
          dropImageSettingsListRef.current,
        );
        updateRegionCaptureProfiles(resp);
        dropImageSettingsListRef.current = null;
        setDropImageSettingsList([]);
      } catch (error) {
        console.error('Error updating region capture profiles:', error);
      }
    }
  };

  const handleClickOutside = (e: MouseEvent) => {
    const target = e.target as Node;
    const isInsideTrigger = wrapperRef.current?.contains(target);
    const isInsideMenu = menuPortalRef.current?.contains(target);
    if (!isInsideTrigger && !isInsideMenu) {
      setIsMenuOpen(false);
      updateRegionProfiles();
    }
  };

  useEffect(() => {
    dropImageSettingsListRef.current = dropImageSettingsList;
  }, [dropImageSettingsList]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      updateRegionProfiles();
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [isMenuOpen, updateMenuPosition]);
  return (
    <div
      id="commandpanel-exptools-dropimagesettingsdropdown-wrapper"
      className="relative inline-block right-1 text-left"
    >
      <RMCustomButton
        Icon={
          <RMSvgIcon
            id="commandpanel-exptools-dropimagesettingsdropdown-descending-icon"
            name="Descending"
            width={'20px'}
            height={'10px'}
            fill="#474747"
            subfolder="Setup"
          />
        }
        onClick={(e) => {
          e.stopPropagation();
          onMenuClick();
        }}
        contentContainerClass="inline-flex justify-center w-full rounded-md"
      />
      {isMenuOpen &&
        menuPosition &&
        createPortal(
          <div
            ref={menuPortalRef}
            id="commandpanel-exptools-dropimagesettingsdropdown-menu"
            className={
              canvasMode
                ? 'fixed z-[9999] w-[270px] rounded-md shadow-lg bg-white'
                : 'fixed z-[9999] w-56 rounded-md shadow-lg bg-white'
            }
            style={{
              top: menuPosition.top,
              right: menuPosition.right,
            }}
          >
            <div
              id="commandpanel-exptools-dropimagesettingsdropdown-menu-options"
              className="py-1"
            >
              {customizeOptions.map((option) => (
                <div
                  id={`commandpanel-exptools-dropimagesettingsdropdown-${option.id}`}
                  key={`imaging-settings-customize-options-${option.id}`}
                  className="relative"
                >
                  <div
                    id={`commandpanel-exptools-dropimagesettingsdropdown-option-${option.label}-label`}
                    onClick={() => handleOptionClick(option)}
                    onMouseOver={
                      option.submenuItems ? () => handleOptionClick(option) : () => {}
                    }
                    className={`flex justify-between align-center block px-4 py-2 text-sm w-full text-left ${
                      option.isDisabled
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-white-700 hover:bg-blue-200'
                    }`}
                  >
                    {option.label}
                    {option.submenuItems && option.submenuItems.length > 0 && (
                      <RMSvgIcon
                        id={`commandpanel-exptools-dropimagesettingsdropdown-arrowright-icon`}
                        name="ArrowRight"
                      />
                    )}
                  </div>

                  {option.submenuItems && option.id === openSubmenuId && (
                    <div
                      id={`commandpanel-exptools-dropimagesettingsdropdown-submenu-${option.id}`}
                      className={`absolute top-0 mt-0 w-56 rounded-md shadow-lg bg-white max-h-[40vh] overflow-y-auto ${
                        canvasMode ? 'left-full ml-2' : 'right-full mr-2'
                      }`}
                    >
                      <div className="py-1">
                        {option.submenuItems.map((subItem) => (
                          <div
                            id={`commandpanel-exptools-dropimagesettingsdropdown-submenu-${option.id}-item-${subItem.label}`}
                            key={'imaging-settings-submenu'}
                            className="flex flex-row block px-4 py-2 text-sm w-full text-left text-white-700 hover:bg-blue-200"
                          >
                            {subItem.checkbox && (
                              <div
                                id={`commandpanel-exptools-dropimagesettingsdropdown-submenu-${option.id}-item-${subItem.label}-checkbox`}
                                className="rm-drop-down-list-row-icon"
                              >
                                {subItem.checkbox}
                              </div>
                            )}
                            {subItem.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default DropImageSettingsDropdown;
